<template>
  <w-sliding-popup ref="slidingPopup" :title="title" width="1250px" @close="close()">
    <div slot="content">
      <el-row type="flex" justify="space-between" :gutter="15">
        <el-col :span="13">
          <el-row class="title-bar" type="flex" justify="space-between">
            <div class="vertical-center-box">
              <div class="title-h3 mr-r15">작업 목록</div>
            </div>
            <div class="vertical-center-box" align="right">
              <w-header-search-button @click="showSearchTool = !showSearchTool"></w-header-search-button>
            </div>
          </el-row>
          <el-table-wrapper
            ref="table"
            :column-default="columnDefault"
            :data="tableData.job"
            :columns="tableColumns.job"
            :show-search-tool="showSearchTool"
            :show-custom-header="true"
            remote
            pagination
            :total="total.job"
            @condition-change="loadData()"
          >
            <template slot-scope="scope" slot="node-slot">
              <span><i class="far fa-search-plus c-pointer" @click.stop="onClickShowNodeButton(scope.row)"></i></span>
            </template>
          </el-table-wrapper>
        </el-col>
        <el-col :span="11">
          <el-row class="title-bar" type="flex" justify="space-between">
            <div class="vertical-center-box">
              <div class="title-h3 mr-r15">{{ nodeTitle }}</div>
            </div>
            <div class="vertical-center-box" align="right">
              <el-button :disabled="selectedJob == null" class="mr-r4" type="small" @click="onClickNodeSettingButton">장비설정</el-button>
              <el-button class="mr-r4" type="small" @click="onClickNodeApplicationButton" :disabled="triggerNodeList.length == 0">일괄적용</el-button>
              <w-header-search-button @click="showNodeSearchTool = !showNodeSearchTool"></w-header-search-button>
            </div>
          </el-row>
          <el-table-wrapper
            ref="nodeTable"
            :column-default="columnDefault"
            :data="tableData.node"
            :columns="tableColumns.node"
            :show-search-tool="showNodeSearchTool"
            :show-custom-header="true"
            remote
            pagination
            :total="total.node"
            @condition-change="loadData('node')"
          >
          </el-table-wrapper>
        </el-col>
      </el-row>
      <w-trigger-node-setting-popup :selected-trigger="selectedTrigger" :selected-trg-job="selectedJob" :trigger-node-list="triggerNodeList" ref="triggerNodeSettingPopup" @re-load="loadJobData" />
      <w-trigger-node-application-popup :table-data="tableDataForApplication" ref="triggerNodeApplicationPopup" />
    </div>
    <div slot="footer">
      <el-button size="large" type="solid" @click="onClickExecuteButton">수행</el-button>
      <el-button size="large" @click="close">취소</el-button>
    </div>
  </w-sliding-popup>
</template>

<script>
import { findIndex, isNil, isEqual, cloneDeep } from 'lodash';
import { getValueAfterCheckEmpty, EMPTY_TEXT } from '@/utils/string';
import msgUtil from '@/utils/message';
import { columnDefault } from '@/constants/table';
import AmsExecutionAPI from '@/api/ams/execution';
import WTriggerNodeSettingPopup from './TriggerNodeSettingPopup';
import WTriggerNodeApplicationPopup from './TriggerNodeApplicationPopup';

const defaultFormatter = (row, column, value) => getValueAfterCheckEmpty(value);

export default {
  name: 'WExecuteDetailPopup',
  components: {
    WTriggerNodeSettingPopup,
    WTriggerNodeApplicationPopup,
  },
  props: {
    selectedTrigger: {
      type: Object,
      default: () => null,
    },
  },
  computed: {
    title() {
      let title = EMPTY_TEXT;
      if (!isNil(this.selectedTrigger)) {
        title = `[${this.selectedTrigger.triggerName}] 수행`;
      }

      return title;
    },
    nodeTitle() {
      let title = '장비 목록';
      if (!isNil(this.selectedJob)) {
        title = `[${this.selectedJob.jobName}] 장비 목록`;
      }
      return title;
    },
  },
  data() {
    return {
      selectedJob: null,
      columnDefault,
      total: {
        job: 0,
        node: 0,
      },
      showSearchTool: false,
      showNodeSearchTool: false,
      tableColumns: {
        job: [
          {
            prop: 'grpName',
            label: '그룹이름',
            searchable: true,
            formatter: defaultFormatter,
          },
          {
            prop: 'jobName',
            label: '작업이름',
            searchable: true,
            formatter: defaultFormatter,
          },
          {
            prop: 'failover',
            label: '실패제어',
            searchable: true,
            formatter: defaultFormatter,
            width: '100px',
          },
          {
            label: '장비보기',
            scopedSlot: 'node-slot',
            width: '65px',
            align: 'center',
          },
        ],
        node: [
          {
            prop: 'nodeIp',
            label: '장비IP',
            searchable: true,
            formatter: defaultFormatter,
          },
          {
            prop: 'nodeName',
            label: '장비이름',
            searchable: true,
            formatter: defaultFormatter,
          },
        ],
      },
      tableData: {
        job: [],
        node: [],
      },
      tableDataForApplication: {
        job: [],
        node: [],
      },
      triggerNodeList: [],
      trgNodeTotal: 0,
    };
  },
  watch: {
    selectedJob(o) {
      if (!isNil(o)) {
        this.loadNodeData();
      }
    },
  },
  methods: {
    open() {
      this.$refs.slidingPopup.visible = true;
      this.$nextTick(() => {
        this.loadData();
      });
    },
    close() {
      this.$refs.slidingPopup.visible = false;
      this.resetData();
    },
    loadData() {
      this.loadJobData();
    },
    loadJobData() {
      const tableParams = this.$refs.table.getParameter();
      const params = {
        triggerId: this.selectedTrigger.triggerId,
        ...tableParams,
      };
      AmsExecutionAPI.getTriggerJobListByTriggerId(params).then((response) => {
        const result = response.data;
        if (result.success) {
          this.total.job = result.total;
          this.tableData.job = result.data;
          if (isNil(this.selectedJob)) {
            this.selectedJob = this.tableData.job[0];
          } else {
            let index = findIndex(this.tableData.job, (item) => item.jobId === this.selectedJob.jobId);
            if (index < 0) {
              index = 0;
            }
            this.selectedJob = this.tableData.job[index];
          }
          this.trgNodeTotal = 0;
          this.tableData.job.forEach((item) => {
            this.trgNodeTotal += item.count;
          });
        }
      });
    },
    loadNodeData() {
      const tableParams = this.$refs.nodeTable.getParameter();
      const params = {
        triggerId: this.selectedJob.triggerId,
        jobId: this.selectedJob.jobId,
        ...tableParams,
      };
      AmsExecutionAPI.getTriggerNodeListByJobId(params).then((response) => {
        const result = response.data;
        if (result.success) {
          this.total.node = result.total;
          this.tableData.node = result.data;
          this.triggerNodeList = cloneDeep(result.data);
        }
      });
    },
    resetData() {
      this.total.job = 0;
      this.tableData.job = [];
      this.total.node = 0;
      this.tableData.node = [];
      this.selectedJob = null;
      this.trgNodeTotal = 0;
    },
    getStatusFlagName(row, column, value) {
      if (isEqual(value, 'C')) {
        return '완료';
      } else if (isEqual(value, 'I')) {
        return '진행중';
      }
      return '대기';
    },
    getResultFlagName(row, column, value) {
      if (isEqual(value, 'S')) {
        return '성공';
      }
      return '실패';
    },
    onClickShowNodeButton(row) {
      this.selectedJob = row;
    },
    onClickNodeSettingButton() {
      this.$refs.triggerNodeSettingPopup.open();
    },
    onClickNodeApplicationButton() {
      this.tableDataForApplication = cloneDeep(this.tableData);
      this.$refs.triggerNodeApplicationPopup.open();
    },
    onClickExecuteButton() {
      // 템플릿 수행 task 시작
      if (this.total.job === 0) {
        this.$message('수행할 작업이 없습니다.');
      } else if (this.trgNodeTotal === 0) {
        this.$message('작업을 수행할 장비를 설정해주십시오');
      } else {
        this.$confirm(...msgUtil.getCustomConfirmData('템플릿을 수행하시겠습니까?')).then(() => {
          AmsExecutionAPI.executeDirectTrigger({
            triggerId: this.selectedTrigger.triggerId,
          })
            .then((response) => {
              if (response.data.success) {
                const triggerExecId = response.data.data;
                this.$store.dispatch('executeTriggerTask', triggerExecId);
                this.$message(msgUtil.getCustomResultMsgData('템플릿을 수행합니다. 대량 장비에 대해 템플릿을 수행할 경우 시간이 다소 소요될 수 있습니다.', true));
                this.close();
              } else {
                this.$message(msgUtil.getResultMsgData('수행', '템플릿', false));
              }
            })
            .catch((e) => {
              this.$message(msgUtil.getResultMsgData('수행', '템플릿', false));
              throw e;
            });
        });
      }
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/views/work/popup/components/ExecuteTrigger/popup/ExecuteDetailPopup.vue